import { getAssetsUrl } from "utils";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/Find/const";
const I18n = I18nUtil.createScoped(Translations);

export enum RootPathEnum {
  // PORTFOLIO_GROUP = "professional-tools",
  ANALYZER = "technical-analysis",
  MEGABRAIN = "megabrain-investor-picks",
  PIVOT = "pivot-alert",
  MINDSHARE = "mindshare",
  CRYPTO = "crypto-news-and-impact",
  MEMECOINS = "find-memecoins",
  HOT = "hot-on-x",
  LATEST = "latest-fundraised",
  MACRO = "macro-decoder",
  ONCHAIN = "on-chain-alpha",
  JUNKIES = "fortune-teller",
  ECOSYSTEM = "ecosystem-whisperer",
  GHOSTWRITER = "tweet-ghostwriter",
  GENERATOR = "bs-generator",
  DETECTOR = "pre-detector",
  STOCKS = "stocks-analyzer",
  REPORT = "rating",
}

export enum PORTFOLIO_QUERY {
  PORTFOLIO = "portfolio-management",
  ANALYZER = "technical-analysis",
  TRACKER = "project-tracker",
}

export enum ProfessionalIDEnum {
  ANALYZER = "analyzer",
  MEGABRAIN = "megabrain",
  PIVOT = "pivot",
  MINDSHARE = "mindshare",
  REPORT = "report",
}

export enum StockIDEnum {
  REPORT = "report",
  ANALYZER = "analyzer",
  MINDSHARE = "mindshare",
  PIVOT = "pivot",
}

export enum SmartIDEnum {
  MACRO = "macro", // Macro Decoder
  STOCKS = "stocks",
  ONCHAIN = "onChain", // On-Chain Sniper
  JUNKIES = "junkies", // TA Fortune Teller
  ECOSYSTEM = "ecosystem", // Ecosystem Whisperer
  GHOSTWRITER = "ghostwriter", // Tweet Ghostwriter
}

export enum BoredIDEnum {
  GENERATOR = "generator", // BS Generator
  DETECTOR = "detector", // Pre-TGE Detector
}

export enum AlphaIDEnum {
  CRYPTO = "crypto",
  MEMECOINS = "meme",
  HOT = "hot_on_x",
  LATEST = "justfundraised",
}

export type AgentIdEnum =
  | AlphaIDEnum
  | SmartIDEnum
  | BoredIDEnum
  | ProfessionalIDEnum;

export const AgentIdEnum = {
  ...ProfessionalIDEnum,
  ...AlphaIDEnum,
  ...SmartIDEnum,
  ...BoredIDEnum,
};

export const pageGroupData: Record<
  string,
  {
    title: string;
  }
> = {
  tool: {
    title: "Professional Tools",
  },
  alpha: {
    title: "Alpha Co-Pilots",
  },
  smart: {
    title: "“Smart”  Co-Pilots",
  },
  cope: {
    title: "Cope-Pilots",
  },
};

export const getPageData = (
  type: string,
): Record<
  string,
  {
    title: string;
    description: string;
    brief: string;
    logo: string;
    id: AgentIdEnum;
    radarPath: string;
    subTitle: string;
    signType: string;
    isPro: boolean;
    isOpened: boolean;
    groupId: string;
    pageIcon: string;
    bgImage: string;
    descriptionClassName?: string;
    className?: string;
    h1Tag?: string;
    metaData: {
      title: string;
      description: string;
    };
  }
> => {
  const pageData: Record<string, Record<string, any>> = {
    crypto: {
      [RootPathEnum.ANALYZER]: {
        title: I18n.t("Technical Signal"),
        brief: I18n.t("Bullish & bearish signals made clear and easy."),
        description: I18n.t(
          "Get razor-sharp signals powered by technical indicators like RSI and MACD, so you know when to enter, exit, and more first.",
        ),
        logo: getAssetsUrl("/assets/images/technical-analysis.webp"),
        id: ProfessionalIDEnum.ANALYZER,
        radarPath: "",
        subTitle: "",
        isPro: true,
        isOpened: true,
        signType: "",
        groupId: "tool",
        pageIcon: "",
        bgImage: getAssetsUrl("/assets/images/market-technical-signal.webp"),
        descriptionClassName: "max-w-[165px]",
        h1Tag: "Technical Analysis for Advanced Crypto Trading",
        metaData: {
          title: "AI-Powered Technical Analysis for Crypto Trading | Edgen",
          description:
            "Leverage Edgen's AI-driven technical analysis tools to decode crypto market trends with real-time data and institutional-grade precision.",
        },
      },
      [RootPathEnum.MINDSHARE]: {
        title: I18n.t("Trading Mindshare"),
        brief: I18n.t("Catch the right short-term price moves quickly."),
        description: I18n.t(
          "Forget Crypto Twitter gossip, real money talks quietly. Discover what tokens really dominate trading volume across top exchanges like Binance, Coinbase, and Upbit, and stay consistently ahead to outsmart the crowd.",
        ),
        logo: getAssetsUrl("/assets/images/chat_macro.webp"),
        id: ProfessionalIDEnum.MINDSHARE,
        radarPath: "",
        subTitle: "",
        signType: "",
        isPro: true,
        isOpened: true,
        groupId: "tool",
        pageIcon: "",
        bgImage: getAssetsUrl("/assets/images/nindshare-icon.webp"),
        descriptionClassName: "max-w-[200px]",
        h1Tag: "Trading Mindshare: Monitor Crypto Exchange Liquidity Live",
        metaData: {
          title:
            "Trading Mindshare: Real-Time Crypto Liquidity Insights | Edgen",
          description:
            "Track real-time coin trading volume shares on Binance, Coinbase, and Upbit with Edgen’s Trading Mindshare for smarter crypto trading.",
        },
      },
      [RootPathEnum.PIVOT]: {
        title: I18n.t("Pivot Alert"),
        brief: I18n.t("Know EXACTLY when to buy or sell."),
        description: I18n.t(
          "Spot local tops and bottoms so you can trade price swings with confidence, not guesswork.",
        ),
        logo: getAssetsUrl("/assets/images/Moonshot.webp"),
        id: ProfessionalIDEnum.PIVOT,
        radarPath: "",
        subTitle: "",
        signType: "",
        isPro: true,
        isOpened: true,
        groupId: "tool",
        pageIcon: "",
        bgImage: getAssetsUrl("/assets/images/pivotalert-icon.webp"),
        descriptionClassName: "max-w-[200px]",
        h1Tag: "Pivot Alert: Real-Time Buy & Sell Signals for Crypto Trading",
        metaData: {
          title: "Pivot Alert: AI-Powered Buy/Sell Signals for Crypto | Edgen",
          description:
            "Get instant buy/sell signals with Edgen's Pivot Alert. AI-driven charts and analysis help you trade crypto smarter, ahead of the market.",
        },
      },
      [RootPathEnum.REPORT]: {
        title: I18n.t("Crypto 360° Report"),
        brief: I18n.t("360° AI-powered analysis & score for any asset."),
        description: I18n.t(
          "Forget boring reports. Our AI goes ultra-deep, serving easy-to-digest insights on fundamentals, technicals, and insiders, with one killer score to rule them all.",
        ),
        logo: getAssetsUrl("/assets/images/report.webp"),
        id: ProfessionalIDEnum.REPORT,
        radarPath: "",
        subTitle: "",
        signType: "",
        isPro: true,
        isOpened: true,
        groupId: "tool",
        pageIcon: "",
        bgImage: getAssetsUrl("/assets/images/crypto-report-icon.webp"),
        descriptionClassName: "max-w-[200px]",
        h1Tag: "Crypto 360 Report",
        metaData: {
          title: "AI Crypto Ratings and Price Targets | Edgen",
          description:
            "Try Edgen's AI analyst ratings and price targets for crypto and memes . See today's crypto ratings by Edgen AI & financial analysts around the world in real-time.",
        },
        className: "bg-background_solid_secondary h-full overflow-hidden pb-0",
      },
      // 1.25添加agent thinking输出之后，和margin会有冲突，暂时下掉入口，调试完成后打开
      // [RootPathEnum.MEGABRAIN]: {
      //   title: I18n.t("Megabrain Investors Picks"),
      //   brief: I18n.t("Chat with AI versions of legendary investors."),
      //   description: I18n.t(
      //     "Ever been roasted by Warren Buffett’s AI twin? Chat with investing legends who actually share their real holdings, authentic strategies, and ruthless honesty, ready to give you brilliant tips or brutally honest feedback about your portfolio and investment ideas.",
      //   ),
      //   logo: getAssetsUrl("/assets/images/megabrain.webp"),
      //   id: ProfessionalIDEnum.MEGABRAIN,
      //   radarPath: "",
      //   subTitle: "",
      //   signType: "",
      //   groupId: "tool",
      //   isPro: false,
      //   isOpened: true,
      //   pageIcon: "",
      //   descriptionClassName: "max-w-[200px]",
      //   bgImage: getAssetsUrl(
      //     "/assets/images/market-megabrain-investors-pick.webp",
      //   ),
      //   className: "max-w-[936px]",
      //   h1Tag: "Megabrain Investor Picks: Top AI-Powered Crypto Investments",
      //   metaData: {
      //     title:
      //       "Megabrain Investor Picks: AI-Driven Crypto Opportunities | Edgen",
      //     description:
      //       "Discover Edgen’s Megabrain Investor Picks—AI-curated crypto investments with real-time insights to maximize your trading edge.",
      //   },
      // },
      [RootPathEnum.GENERATOR]: {
        title: I18n.t("BS Generator"),
        brief: I18n.t(
          "Casual conversations and humor with Edgen AI for when serious topics can wait!",
        ),
        description: I18n.t(
          "Casual conversations and humor with Edgen AI for when serious topics can wait!",
        ),
        logo: getAssetsUrl("/assets/images/chat_generator.webp"),
        id: AgentIdEnum.GENERATOR,
        radarPath: "",
        subTitle: "",
        signType: "",
        isPro: false,
        isOpened: true,
        groupId: "cope",
        pageIcon: getAssetsUrl("/assets/images/agent_bs.webp"),
        descriptionClassName: "max-w-[200px]",
        bgImage: getAssetsUrl("/assets/images/generator-Icon.webp"),
        h1Tag: "BS Generator: Create Crypto Hype with AI",
        metaData: {
          title: "BS Generator: AI-Powered Crypto Hype Creator | Edgen",
          description:
            "Edgen’s BS Generator crafts crypto buzzwords and hype-filled tweets with AI, exposing market noise or boosting your social game.",
        },
      },
      [RootPathEnum.ONCHAIN]: {
        title: I18n.t("On-Chain Sniper"),
        brief: I18n.t(
          "On-chain moves revealed. Track wallets and transactions smarter than your trading group.",
        ),
        description: I18n.t(
          "On-chain moves revealed. Track wallets and transactions smarter than your trading group.",
        ),
        logo: getAssetsUrl("/assets/images/chat_onChain.webp"),
        id: AgentIdEnum.ONCHAIN,
        radarPath: "",
        subTitle: "",
        signType: "",
        isPro: false,
        isOpened: true,
        groupId: "smart",
        pageIcon: getAssetsUrl("/assets/images/agent_sniper.webp"),
        descriptionClassName: "max-w-[200px]",
        bgImage: getAssetsUrl("/assets/images/market-on-chain-sniper.webp"),
        h1Tag: "On-Chain Alpha: Discover Winning Crypto Trades with AI",
        metaData: {
          title: "On-Chain Alpha: AI-Powered Crypto Trading Insights | Edgen",
          description:
            "Unlock Edgen’s On-Chain Alpha: AI-driven insights from blockchain data to spot crypto trends and trade smarter, faster than the market.",
        },
      },
      [RootPathEnum.GHOSTWRITER]: {
        title: I18n.t("Tweet Ghostwriter"),
        brief: I18n.t(
          "Instantly generate tweets in any style and save time creating content!",
        ),
        description: I18n.t(
          "Savage tweets of any tone cooked fresh. Because your drafts are just like your imagination: empty.",
        ),
        logo: getAssetsUrl("/assets/images/chat_tweet.webp"),
        id: AgentIdEnum.GHOSTWRITER,
        radarPath: "",
        subTitle: "",
        signType: "",
        groupId: "smart",
        isPro: false,
        isOpened: true,
        pageIcon: getAssetsUrl("/assets/images/agent_ghostwriter.webp"),
        descriptionClassName: "max-w-[200px]",
        bgImage: getAssetsUrl("/assets/images/ghostwriter-icon.webp"),
        h1Tag: "Tweet Ghostwriter: AI-Powered Tweets for Crypto Success",
        metaData: {
          title: "Tweet Ghostwriter: AI-Crafted Crypto Tweets | Edgen",
          description:
            "Edgen’s Tweet Ghostwriter creates on-brand, engaging crypto tweets using AI, boosting your Twitter presence with real-time market insights.",
        },
      },
      [RootPathEnum.DETECTOR]: {
        title: I18n.t("Pre-TGE Detector"),
        brief: I18n.t(
          "Identify pre-launch opportunities and become an early adopter!",
        ),
        description: I18n.t(
          "Identify pre-launch opportunities and become an early adopter!",
        ),
        logo: getAssetsUrl("/assets/images/chat_detector.webp"),
        id: AgentIdEnum.DETECTOR,
        radarPath: "",
        subTitle: "",
        signType: "",
        isPro: false,
        isOpened: true,
        groupId: "cope",
        pageIcon: getAssetsUrl("/assets/images/agent_detector.webp"),
        descriptionClassName: "max-w-[200px]",
        bgImage: getAssetsUrl("/assets/images/detector-icon.webp"),
        h1Tag: "Pre-Detector: Catch Crypto Trends Before They Break",
        metaData: {
          title: "Pre-Detector: AI-Powered Early Crypto Signals | Edgen",
          description:
            "Edgen’s Pre-Detector uses AI to spot crypto market signals early—whale moves, pumps, or trends—so you can trade ahead of the crowd.",
        },
      },
    },
    "us-stock": {
      [RootPathEnum.REPORT]: {
        title: I18n.t("Stocks 360° Report"),
        brief: I18n.t("360° AI-powered analysis & score for an asset."),
        description: I18n.t(
          "Forget boring reports. Our AI goes ultra-deep, serving easy-to-digest insights on fundamentals, technicals, and insiders, with one killer score to rule them all.",
        ),
        logo: getAssetsUrl("/assets/images/report.webp"),
        id: StockIDEnum.REPORT,
        radarPath: "",
        subTitle: "",
        signType: "",
        isPro: true,
        isOpened: true,
        groupId: "tool",
        pageIcon: "",
        bgImage: getAssetsUrl("/assets/images/stock-report-icon.webp"),
        descriptionClassName: "max-w-[200px]",
        h1Tag: "Stock 360 Report",
        metaData: {
          title: "AI Stock Ratings and Price Targets | Edgen",
          description:
            "Try Edgen's AI analyst ratings and price targets for US stocks . See today's stock ratings by Edgen AI & financial analysts around the world in real-time.",
        },
        // className: "bg-background_solid_secondary h-full overflow-hidden pb-0",
      },
      [RootPathEnum.ANALYZER]: {
        title: I18n.t("Technical Signal"),
        brief: I18n.t("Bullish & bearish signals made clear and easy."),
        description: I18n.t(
          "Trade smarter and instantly spot clear price trends, optimal entry points, precise exits, and market sentiment (Bullish or Bearish) backed by razor sharp technical indicators like MACD or RSI, so you navigate markets decisively and profitably.",
        ),
        logo: getAssetsUrl("/assets/images/stock-analysis-icon.webp"),
        id: StockIDEnum.ANALYZER,
        radarPath: "",
        subTitle: "",
        isPro: true,
        signType: "",
        isOpened: false,
        groupId: "tool",
        pageIcon: "",
        bgImage: getAssetsUrl("/assets/images/stock-analysis-icon.webp"),
        descriptionClassName: "max-w-[165px]",
        h1Tag: "Technical Analysis for Advanced Crypto Trading",
        metaData: {
          title: "AI-Powered Technical Analysis for Crypto Trading | Edgen",
          description:
            "Leverage Edgen's AI-driven technical analysis tools to decode crypto market trends with real-time data and institutional-grade precision.",
        },
      },
      [RootPathEnum.PIVOT]: {
        title: I18n.t("Pivot Alert"),
        brief: I18n.t("Know EXACTLY when to buy or sell."),
        description: I18n.t(
          "Stop chasing candles: let AI mail the perfect trade timing for you with buy and sell points. Target local tops and bottoms, and turn market swings into predictable profits.",
        ),
        logo: getAssetsUrl("/assets/images/Moonshot.webp"),
        id: StockIDEnum.PIVOT,
        radarPath: "",
        subTitle: "",
        signType: "",
        isPro: true,
        isOpened: false,
        groupId: "tool",
        pageIcon: "",
        bgImage: getAssetsUrl("/assets/images/stock-pivotalert-icon.webp"),
        descriptionClassName: "max-w-[200px]",
        h1Tag: "Pivot Alert: Real-Time Buy & Sell Signals for Crypto Trading",
        metaData: {
          title: "Pivot Alert: AI-Powered Buy/Sell Signals for Crypto | Edgen",
          description:
            "Get instant buy/sell signals with Edgen's Pivot Alert. AI-driven charts and analysis help you trade crypto smarter, ahead of the market.",
        },
      },
      [RootPathEnum.MINDSHARE]: {
        title: I18n.t("Trading Mindshare"),
        brief: I18n.t("Catch the right short-term price moves quickly."),
        description: I18n.t(
          "Know exactly where the money’s moving. Track tokens gaining attention on Binance, Coinbase, or Upbit, measured by each exchange's share of total trading volume.",
        ),
        logo: getAssetsUrl("/assets/images/chat_macro.webp"),
        id: StockIDEnum.MINDSHARE,
        radarPath: "",
        subTitle: "",
        signType: "",
        isPro: false,
        isOpened: false,
        groupId: "tool",
        pageIcon: "",
        bgImage: getAssetsUrl("/assets/images/stock-mindshare-icon.webp"),
        descriptionClassName: "max-w-[200px]",
        h1Tag: "Trading Mindshare: Monitor Crypto Exchange Liquidity Live",
        metaData: {
          title:
            "Trading Mindshare: Real-Time Crypto Liquidity Insights | Edgen",
          description:
            "Track real-time coin trading volume shares on Binance, Coinbase, and Upbit with Edgen’s Trading Mindshare for smarter crypto trading.",
        },
      },
      [RootPathEnum.MEGABRAIN]: {
        title: I18n.t("Megabrain Investors Picks"),
        brief: I18n.t("Chat with AI versions of legendary investors."),
        description: I18n.t(
          "Ever been roasted by Warren Buffett’s AI twin? Chat with investing legends who actually share their real holdings, authentic strategies, and ruthless honesty, ready to give you brilliant tips or brutally honest feedback about your portfolio and investment ideas.",
        ),
        logo: getAssetsUrl("/assets/images/megabrain.webp"),
        id: ProfessionalIDEnum.MEGABRAIN,
        radarPath: "",
        subTitle: "",
        signType: "",
        groupId: "tool",
        isPro: false,
        isOpened: false,
        pageIcon: "",
        descriptionClassName: "max-w-[200px]",
        bgImage: getAssetsUrl("/assets/images/stock-megabrain-icon.webp"),
        className: "max-w-[936px]",
        h1Tag: "Megabrain Investor Picks: Top AI-Powered Crypto Investments",
        metaData: {
          title:
            "Megabrain Investor Picks: AI-Driven Crypto Opportunities | Edgen",
          description:
            "Discover Edgen’s Megabrain Investor Picks—AI-curated crypto investments with real-time insights to maximize your trading edge.",
        },
      },
    },
  };
  return pageData[type as keyof typeof pageData] || {};
};

export const getDefaultQuestions = () => {
  const I18n = I18nUtil.createScoped(Translations);
  return {
    [AgentIdEnum.MACRO]: [
      {
        title: I18n.t("How is recession affecting BTC?"),
        agent_id: AgentIdEnum.MACRO,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Is China's economy still bullish?"),
        agent_id: AgentIdEnum.MACRO,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Why did CPI shake markets?"),
        agent_id: AgentIdEnum.MACRO,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Why does macro always nuke my bags?"),
        agent_id: AgentIdEnum.MACRO,
        scopes: ["AGENT"],
      },
    ],
    [AgentIdEnum.STOCKS]: [
      {
        title: I18n.t("Explain $AAPL's latest earning report"),
        agent_id: AgentIdEnum.STOCKS,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("$TSLA: buy or sell?"),
        agent_id: AgentIdEnum.STOCKS,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Any boomer stocks worth watching?"),
        agent_id: AgentIdEnum.STOCKS,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Why did $NVDA pump today?"),
        agent_id: AgentIdEnum.STOCKS,
        scopes: ["AGENT"],
      },
    ],
    [AgentIdEnum.ONCHAIN]: [
      {
        title: I18n.t("Any smart money buying ETH recently?"),
        agent_id: AgentIdEnum.ONCHAIN,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Track biggest DeFi winners."),
        agent_id: AgentIdEnum.ONCHAIN,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Who's secretly dumping tokens today?"),
        agent_id: AgentIdEnum.ONCHAIN,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Give me a low-cap, high liqudity token"),
        agent_id: AgentIdEnum.ONCHAIN,
        scopes: ["AGENT"],
      },
    ],
    [AgentIdEnum.JUNKIES]: [
      {
        title: I18n.t("Is TA just astrology for men?"),
        agent_id: AgentIdEnum.JUNKIES,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("ETH breakout or fakeout?"),
        agent_id: AgentIdEnum.JUNKIES,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Explain RSI to a degen"),
        agent_id: AgentIdEnum.JUNKIES,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Next support level for SOL?"),
        agent_id: AgentIdEnum.JUNKIES,
        scopes: ["AGENT"],
      },
    ],
    [AgentIdEnum.ECOSYSTEM]: [
      {
        title: I18n.t("Best projects on Arbitrum?"),
        agent_id: AgentIdEnum.ECOSYSTEM,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Explain the Cosmos ecosystem for me."),
        agent_id: AgentIdEnum.ECOSYSTEM,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Explain Cardano without laughing"),
        agent_id: AgentIdEnum.ECOSYSTEM,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("What happened to Fantom?"),
        agent_id: AgentIdEnum.ECOSYSTEM,
        scopes: ["AGENT"],
      },
    ],
    [AgentIdEnum.GHOSTWRITER]: [
      {
        title: I18n.t("Write a rugpull coping tweet"),
        agent_id: AgentIdEnum.GHOSTWRITER,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Tweet like Donald Trump"),
        agent_id: AgentIdEnum.GHOSTWRITER,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Joke about exit liquidity"),
        agent_id: AgentIdEnum.GHOSTWRITER,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Write down a hot take/unpopular opinion"),
        agent_id: AgentIdEnum.GHOSTWRITER,
        scopes: ["AGENT"],
      },
    ],
    [AgentIdEnum.GENERATOR]: [
      {
        title: I18n.t("How are you?"),
        agent_id: AgentIdEnum.GENERATOR,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Can I be your boyfriend?"),
        agent_id: AgentIdEnum.GENERATOR,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Give me excuses to skip family events."),
        agent_id: AgentIdEnum.GENERATOR,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Roast me."),
        agent_id: AgentIdEnum.GENERATOR,
        scopes: ["AGENT"],
      },
    ],
    [AgentIdEnum.DETECTOR]: [
      {
        title: I18n.t("Which tokens are raising right now?"),
        agent_id: AgentIdEnum.DETECTOR,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Safe pre-TGE bets today?"),
        agent_id: AgentIdEnum.DETECTOR,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Any airdrop I should look forward to?"),
        agent_id: AgentIdEnum.DETECTOR,
        scopes: ["AGENT"],
      },
      {
        title: I18n.t("Upcoming TGEs that will liquidate me?"),
        agent_id: AgentIdEnum.DETECTOR,
        scopes: ["AGENT"],
      },
    ],
  };
};
